import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Mail, Search, Plus, Edit, Trash2, Eye, Send,
  CheckCircle, XCircle, Code, FileText, MoreVertical,
  Copy, Download, X
} from 'lucide-react';
import toast from 'react-hot-toast';
import emailTemplateService from '../services/emailTemplateService';
import RichTextEditor from '../components/RichTextEditor';
import DOMPurify from 'isomorphic-dompurify';
import { CreateButton, SaveButton, UpdateButton, DeleteButton, CancelButton, ViewButton } from '../components/CommonButtons';
import Switch from '../components/Switch';
import CommonLoader, { TableLoader, ListLoader } from '../components/CommonLoader';
import { validateRequired, ErrorMessage } from '../utils/validation';
import FilterBar from '../components/FilterBar';
import StandardDataTable from '../components/StandardDataTable';
import ResponsiveDataTable from '../components/ResponsiveDataTable';
import EnhancedDataTable from '../components/EnhancedDataTableV2';
import EnhancedModal from '../components/EnhancedModal';

const EmailTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showTestEmailModal, setShowTestEmailModal] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [previewContent, setPreviewContent] = useState('');
  const [testEmailData, setTestEmailData] = useState({ email: '' });
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [previewData, setPreviewData] = useState({});

  // Form state for create/edit
  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    content: '',
    placeholders: [],
    is_active: 1  // Number: 1 for Active (checked), 0 for Inactive (unchecked)
  });
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    fetchTemplates();
  }, [currentPage, selectedStatus]);

  const fetchTemplates = async (page = currentPage) => {
    try {
      setLoading(true);
      const params = {
        page,
        is_active: selectedStatus  // Add status filter parameter
      };
      const response = await emailTemplateService.getEmailTemplates(params);
      
      // Debug: Log the raw response to understand the data structure
      
      // Use normalized response structure and process placeholders
      const processedTemplates = (response.data || []).map(template => {
        let placeholders = [];
        
        try {
          if (Array.isArray(template.placeholders)) {
            placeholders = template.placeholders;
          } else if (template.placeholders) {
            if (typeof template.placeholders === 'string') {
              // Try to parse as JSON, fallback to empty array if fails
              placeholders = template.placeholders.trim().startsWith('[') 
                ? JSON.parse(template.placeholders) 
                : [];
            } else {
              placeholders = [];
            }
          }
        } catch (error) {
          placeholders = [];
        }
        
        return {
          ...template,
          placeholders
        };
      });
      
      setTemplates(processedTemplates);
      setPagination(response.pagination);
    } catch (error) {
      toast.error('Failed to fetch email templates');
    } finally {
      setLoading(false);
    }
  };

  const validateTemplateForm = () => {
    const errors = {};
    
    // Name validation
    const nameError = validateRequired(formData.name, 'Template name');
    if (nameError) errors.name = nameError;
    
    // Subject validation
    const subjectError = validateRequired(formData.subject, 'Subject');
    if (subjectError) errors.subject = subjectError;
    
    // Content validation
    const contentError = validateRequired(formData.content, 'Content');
    if (contentError) errors.content = contentError;
    
    // Status validation - ensure it's either 0 or 1
    if (formData.is_active !== 0 && formData.is_active !== 1) {
      errors.is_active = 'Invalid status value';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateTemplate = async () => {
    if (!validateTemplateForm()) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    try {
      await emailTemplateService.createEmailTemplate(formData);
      toast.success('Email template created successfully');
      setShowEditModal(false);
      resetForm();
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to create email template');
    }
  };

  const handleUpdateTemplate = async () => {
    if (!validateTemplateForm()) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    try {
      await emailTemplateService.updateEmailTemplate(selectedTemplate.id, formData);
      toast.success('Email template updated successfully');
      setShowEditModal(false);
      setSelectedTemplate(null);
      resetForm();
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to update email template');
    }
  };

  const handleDeleteTemplate = async () => {
    try {
      await emailTemplateService.deleteEmailTemplate(templateToDelete.id);
      toast.success('Email template deleted successfully');
      setShowDeleteModal(false);
      setTemplateToDelete(null);
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to delete email template');
    }
  };

  const handleToggleStatus = async (templateId) => {
    try {
      // Find the template to get current status
      const template = templates.find(t => t.id === templateId);
      if (!template) return;

      // Toggle the status
      const newStatus = template.is_active ? 0 : 1;

      // Send only the required fields for update
      await emailTemplateService.updateEmailTemplate(templateId, {
        name: template.name,
        subject: template.subject,
        content: template.content,
        is_active: newStatus
      });

      toast.success(`Template ${newStatus ? 'activated' : 'deactivated'} successfully`);
      fetchTemplates();
    } catch (error) {
      console.error('Toggle status error:', error);
      toast.error('Failed to update template status');
    }
  };

  const handlePreviewTemplate = async (template) => {
    try {
      // Initialize preview data with placeholder defaults
      const defaultData = {};
      if (template.placeholders) {
        template.placeholders.forEach(placeholder => {
          // Set some default values for common placeholders
          switch(placeholder.toLowerCase()) {
            case 'name':
            case 'username':
              defaultData[placeholder] = 'John Doe';
              break;
            case 'email':
              defaultData[placeholder] = 'john@example.com';
              break;
            case 'company':
            case 'company_name':
              defaultData[placeholder] = 'Example Company';
              break;
            case 'dataroom_name':
              defaultData[placeholder] = 'Sample Dataroom';
              break;
            case 'otp':
              defaultData[placeholder] = '123456';
              break;
            default:
              defaultData[placeholder] = `{{${placeholder}}}`;
          }
        });
      }
      
      setPreviewData(defaultData);
      const response = await emailTemplateService.previewEmailTemplate(template.id, defaultData);
      // Handle different response formats
      const content = typeof response === 'string' ? response : (response.data || response.content || response.preview || 'No preview available');
      setPreviewContent(content);
      setSelectedTemplate(template);
      setShowPreviewModal(true);
    } catch (error) {
      toast.error('Failed to preview email template');
    }
  };

  const handleTestEmail = async () => {
    try {
      await emailTemplateService.testSendEmail(testEmailData);
      toast.success('Test email sent successfully');
      setShowTestEmailModal(false);
      setTestEmailData({ email: '' });
    } catch (error) {
      toast.error('Failed to send test email');
    }
  };

  const handleEdit = (template) => {
    setSelectedTemplate(template);
    setFormData({
      name: template.name,
      subject: template.subject,
      content: template.content,
      placeholders: template.placeholders || [],
      is_active: template.is_active ? 1 : 0  // Convert to number: 1 for checked, 0 for unchecked
    });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      subject: '',
      content: '',
      placeholders: [],
      is_active: 1  // Default to Active (checked)
    });
    setFormErrors({});
    setSelectedTemplate(null);
  };

  const filteredTemplates = templates.filter(template =>
    template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    template.subject.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  // Extract placeholders from content
  const extractPlaceholders = (content) => {
    const regex = /\{\{(\w+)\}\}/g;
    const matches = [...content.matchAll(regex)];
    return [...new Set(matches.map(match => match[1]))];
  };

  // Update placeholders when content changes
  const handleContentChange = (content) => {
    const placeholders = extractPlaceholders(content);
    setFormData({ ...formData, content, placeholders });
  };

  // Get placeholder description for tooltips
  const getPlaceholderDescription = (placeholder) => {
    const descriptions = {
      'name': 'User\'s full name',
      'username': 'User\'s username',
      'email': 'User\'s email address',
      'company': 'Company name',
      'company_name': 'Company name',
      'dataroom_name': 'Name of the dataroom',
      'admin_name': 'Administrator\'s name',
      'admin_email': 'Administrator\'s email',
      'created_at': 'Creation date',
      'approved_at': 'Approval date',
      'pricing_plan_name': 'Pricing plan name',
      'otp': 'One-time password',
      'subscription_id': 'Subscription ID',
      'first_name': 'User\'s first name',
      'last_name': 'User\'s last name',
      'phone': 'User\'s phone number',
      'role': 'User\'s role',
      'status': 'Status information',
      'date': 'Date information',
      'time': 'Time information',
      'url': 'URL link',
      'link': 'Link URL',
      'password': 'Password information',
      'token': 'Authentication token'
    };
    return descriptions[placeholder.toLowerCase()] || `Insert ${placeholder}`;
  };

  return (
    <div>
      <style>{`
        .prose {
          color: #374151;
          max-width: 100%;
        }
        .prose h1 { font-size: 2em; margin: 0.67em 0; font-weight: bold; }
        .prose h2 { font-size: 1.5em; margin: 0.75em 0; font-weight: bold; }
        .prose h3 { font-size: 1.17em; margin: 0.83em 0; font-weight: bold; }
        .prose h4 { font-size: 1em; margin: 1.12em 0; font-weight: bold; }
        .prose h5 { font-size: 0.83em; margin: 1.5em 0; font-weight: bold; }
        .prose h6 { font-size: 0.75em; margin: 1.67em 0; font-weight: bold; }
        .prose p { margin: 1em 0; line-height: 1.6; }
        .prose strong { font-weight: bold; }
        .prose em { font-style: italic; }
        .prose ul { list-style-type: disc; margin: 1em 0; padding-left: 2em; }
        .prose ol { list-style-type: decimal; margin: 1em 0; padding-left: 2em; }
        .prose li { margin: 0.5em 0; }
        .prose blockquote { 
          border-left: 4px solid #e5e7eb; 
          padding-left: 1em; 
          margin: 1em 0;
          color: #6b7280;
        }
        .prose a { color: #3b82f6; text-decoration: underline; }
        .prose a:hover { color: #2563eb; }
        .prose code { 
          background-color: #f3f4f6; 
          padding: 0.2em 0.4em; 
          border-radius: 3px;
          font-family: monospace;
          font-size: 0.875em;
        }
        .prose pre { 
          background-color: #f3f4f6; 
          padding: 1em; 
          border-radius: 6px;
          overflow-x: auto;
          margin: 1em 0;
        }
        .prose pre code {
          background-color: transparent;
          padding: 0;
        }
        .prose img { max-width: 100%; height: auto; }
        .prose hr { border: 0; border-top: 1px solid #e5e7eb; margin: 2em 0; }
        .prose table { border-collapse: collapse; width: 100%; margin: 1em 0; }
        .prose th, .prose td { 
          border: 1px solid #e5e7eb; 
          padding: 0.5em 1em;
          text-align: left;
        }
        .prose th { background-color: #f9fafb; font-weight: bold; }
      `}</style>
      {/* Page Header */}
      <div className="mb-6 sm:mb-8">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Email Templates</h1>
        <p className="text-sm text-gray-600 mt-1">Manage email templates for system notifications</p>
      </div>

      {/* Action Bar */}
      <div className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-center sm:justify-between mb-6">
          <div className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-center flex-1">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search templates..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
              />
            </div>

            {/* Status Filter */}
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm bg-white hover:border-gray-400 transition-colors appearance-none cursor-pointer shadow-sm"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236B7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                backgroundPosition: 'right 0.5rem center',
                backgroundRepeat: 'no-repeat',
                backgroundSize: '1.25rem'
              }}
            >
              <option value="">All Status</option>
              <option value="1">Active</option>
              <option value="0">Inactive</option>
            </select>

            {/* Test Email */}
            <button
              onClick={() => setShowTestEmailModal(true)}
              className="px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-2 text-sm"
            >
              <Send className="w-4 h-4" />
              <span>Test Email</span>
            </button>
          </div>

          {/* Add Template */}
          <CreateButton
            onClick={() => {
              resetForm();
              setShowEditModal(true);
            }}
            size="sm"
          >
            Add Template
          </CreateButton>
      </div>

      {/* Templates Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <EnhancedDataTable
          data={filteredTemplates}
          columns={[
            {
              key: 'name',
              header: 'Template',
              sortable: true,
              render: (value, row) => (
                <div className="flex items-center">
                  <div className="flex-shrink-0 h-10 w-10">
                    <div className="h-10 w-10 rounded-lg bg-primary-100 flex items-center justify-center">
                      <Mail className="h-5 w-5 text-primary-600" />
                    </div>
                  </div>
                  <div className="ml-4">
                    <div className="text-sm font-medium text-gray-900">{row.name}</div>
                    <div className="text-sm text-gray-500">ID: #{row.id}</div>
                  </div>
                </div>
              )
            },
            {
              key: 'subject',
              header: 'Subject',
              sortable: true,
              render: (value) => (
                <div className="text-sm text-gray-900 max-w-xs truncate">{value}</div>
              )
            },
            {
              key: 'is_active',
              header: 'Status',
              sortable: true,
              render: (value, template) => (
                <Switch
                  checked={template.is_active === 1 || template.is_active === true}
                  onChange={() => handleToggleStatus(template.id)}
                  tooltip={template.is_active ? 'Click to deactivate' : 'Click to activate'}
                />
              )
            }
          ]}
          actions={(row) => [
            {
              icon: Edit,
              label: 'Edit',
              onClick: () => handleEdit(row)
            },
            {
              icon: Trash2,
              label: 'Delete',
              onClick: () => {
                setTemplateToDelete(row);
                setShowDeleteModal(true);
              },
              className: 'text-red-600 hover:text-red-800'
            }
          ]}
          loading={loading}
          emptyMessage="No templates found"
          emptyIcon={Mail}
          showColumnControls={true}
          enableColumnResize={true}
          enableDragDrop={true}
          onSort={(key, direction) => {
            const sorted = [...filteredTemplates].sort((a, b) => {
              const aVal = a[key];
              const bVal = b[key];
              
              if (aVal < bVal) return direction === 'asc' ? -1 : 1;
              if (aVal > bVal) return direction === 'asc' ? 1 : -1;
              return 0;
            });
            setTemplates(sorted);
          }}
        />
      </div>

      {/* Pagination - Responsive */}
      {pagination && pagination.last_page > 1 && (
        <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="text-sm text-gray-700 text-center sm:text-left">
            Showing <span className="font-medium">{pagination.from}</span> to{' '}
            <span className="font-medium">{pagination.to}</span> of{' '}
            <span className="font-medium">{pagination.total}</span> templates
          </div>
          
          <div className="flex items-center gap-1 sm:gap-2">
            <button
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-2 sm: px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:border-transparent"
            >
              <span className="hidden sm:inline">Previous</span>
              <span className="sm:hidden">Prev</span>
            </button>
            
            {/* Page numbers - Responsive */}
            <div className="flex gap-1">
              {[...Array(pagination.last_page)].map((_, index) => {
                const page = index + 1;
                // On mobile, show fewer page numbers
                const isMobileVisible = page === 1 || page === pagination.last_page || page === currentPage;
                const isDesktopVisible = page === 1 || page === pagination.last_page || (page >= currentPage - 1 && page <= currentPage + 1);
                
                if (isDesktopVisible) {
                  return (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`px-2 sm:px-3 py-1.5 focus:border-transparent text-sm rounded-lg transition-colors ${
                        page === currentPage
                          ? 'bg-primary-600 text-white'
                          : 'border border-gray-300 hover:bg-gray-50'
                      } ${
                        !isMobileVisible ? 'hidden sm:block' : ''
                      }`}
                    >
                      {page}
                    </button>
                  );
                } else if (page === currentPage - 2 || page === currentPage + 2) {
                  return <span key={page} className="px-1 sm:px-3 py-1.5 hidden sm:block">...</span>;
                }
                return null;
              })}
            </div>
            
            <button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === pagination.last_page}
              className="px-2 sm: px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:border-transparent"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Edit/Create Modal */}
      <EnhancedModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          resetForm();
        }}
        title={selectedTemplate ? 'Edit Template' : 'Create Template'}
        size="xlarge"
        closeOnEscape={true}
        closeOnOutsideClick={true}
        showCloseButton={true}
      >
        <div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Template Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => {
                    setFormData({ ...formData, name: e.target.value });
                    if (formErrors.name) {
                      setFormErrors({ ...formErrors, name: '' });
                    }
                  }}
                  className={`w-full px-3 py-1.5 focus:border-transparent text-sm border rounded-lg focus:outline-none focus:ring-2 ${
                    formErrors.name ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-primary-500'
                  }`}
                  placeholder="e.g., welcome_email"
                  required
                />
                {formErrors.name && <ErrorMessage error={formErrors.name} />}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Subject Line <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.subject}
                  onChange={(e) => {
                    setFormData({ ...formData, subject: e.target.value });
                    if (formErrors.subject) {
                      setFormErrors({ ...formErrors, subject: '' });
                    }
                  }}
                  className={`w-full px-3 py-1.5 focus:border-transparent text-sm border rounded-lg focus:outline-none focus:ring-2 ${
                    formErrors.subject ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-primary-500'
                  }`}
                  placeholder="e.g., Welcome to {{company_name}}!"
                  required
                />
                {formErrors.subject && <ErrorMessage error={formErrors.subject} />}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Content <span className="text-red-500">*</span>
                </label>
                <div className={formErrors.content ? 'ring-2 ring-red-500 rounded-lg' : ''}>
                  <RichTextEditor
                    value={formData.content}
                    onChange={(value) => {
                      handleContentChange(value);
                      if (formErrors.content) {
                        setFormErrors({ ...formErrors, content: '' });
                      }
                    }}
                    placeholder="Enter your email template content..."
                    placeholders={selectedTemplate ? selectedTemplate.placeholders || [] : [
                      'name', 'username', 'email', 'company', 'company_name',
                      'dataroom_name', 'admin_name', 'admin_email', 'created_at',
                      'approved_at', 'pricing_plan_name', 'otp', 'subscription_id'
                    ]}
                  />
                </div>
                {formErrors.content && <ErrorMessage error={formErrors.content} />}
                <p className="mt-1 text-xs text-gray-500">
                  Use the placeholder buttons above to insert variables into your template.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status
                </label>
                <Switch
                  checked={formData.is_active === 1}
                  onChange={(value) => setFormData({ ...formData, is_active: value ? 1 : 0 })}
                  label={formData.is_active === 1 ? 'Active Template' : 'Inactive Template'}
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <CancelButton
                onClick={() => {
                  setShowEditModal(false);
                  resetForm();
                }}
              />
              {selectedTemplate ? (
                <UpdateButton
                  onClick={handleUpdateTemplate}
                >
                  Update
                </UpdateButton>
              ) : (
                <SaveButton
                  onClick={handleCreateTemplate}
                >
                  Create
                </SaveButton>
              )}
            </div>
        </div>
      </EnhancedModal>

      {/* Preview Modal */}
      <EnhancedModal
        isOpen={showPreviewModal}
        onClose={() => setShowPreviewModal(false)}
        title={selectedTemplate?.name}
        size="xlarge"
        closeOnEscape={true}
        closeOnOutsideClick={true}
        showCloseButton={true}
      >
        <div>
            <div className="flex justify-between items-center mb-4">
              <button
                onClick={() => copyToClipboard(previewContent)}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors mr-10"
                title="Copy to clipboard"
              >
                <Copy className="w-5 h-5" />
              </button>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Placeholder Values */}
              {selectedTemplate?.placeholders && selectedTemplate.placeholders.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Placeholder Values</h4>
                  <div className="space-y-3">
                    {selectedTemplate.placeholders.map((placeholder) => (
                      <div key={placeholder}>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          {placeholder}
                        </label>
                        <input
                          type="text"
                          value={previewData[placeholder] || ''}
                          onChange={(e) => setPreviewData({
                            ...previewData,
                            [placeholder]: e.target.value
                          })}
                          className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:border-transparent focus:ring-2 focus:ring-primary-500 text-sm"
                          placeholder={`Enter ${placeholder}`}
                        />
                      </div>
                    ))}
                    <ViewButton
                      onClick={() => handlePreviewTemplate(selectedTemplate)}
                      className="w-full text-sm"
                    >
                      Update Preview
                    </ViewButton>
                  </div>
                </div>
              )}
              
              {/* Preview Content */}
              <div className={selectedTemplate?.placeholders?.length > 0 ? '' : 'col-span-2'}>
                <h4 className="text-sm font-medium text-gray-700 mb-3">Preview</h4>
                <div className="bg-gray-50 rounded-lg p-4 mb-4">
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">Subject:</span> {selectedTemplate?.subject}
                  </p>
                </div>
                <div className="bg-white border border-gray-200 rounded-lg p-6 overflow-x-auto">
                  {/* Render HTML content safely */}
                  <div 
                    className="prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{ 
                      __html: DOMPurify.sanitize(previewContent, {
                        ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 
                                      'ul', 'ol', 'li', 'blockquote', 'a', 'img', 'code', 'pre', 'hr',
                                      'table', 'thead', 'tbody', 'tr', 'th', 'td', 'div', 'span'],
                        ALLOWED_ATTR: ['href', 'src', 'alt', 'class', 'style', 'target', 'rel']
                      })
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowPreviewModal(false)}
                className="px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm"
              >
                Close
              </button>
            </div>
        </div>
      </EnhancedModal>

      {/* Test Email Modal */}
      <EnhancedModal
        isOpen={showTestEmailModal}
        onClose={() => {
          setShowTestEmailModal(false);
          setTestEmailData({ email: '' });
        }}
        title="Send Test Email"
        size="medium"
        closeOnEscape={true}
        closeOnOutsideClick={true}
        showCloseButton={true}
      >
        <div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Recipient Email
                </label>
                <input
                  type="email"
                  value={testEmailData.email}
                  onChange={(e) => setTestEmailData({ ...testEmailData, email: e.target.value.toLowerCase() })}
                  className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:border-transparent focus:ring-2 focus:ring-primary-500 text-sm"
                  placeholder="test@example.com"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <CancelButton
                onClick={() => {
                  setShowTestEmailModal(false);
                  setTestEmailData({ email: '' });
                }}
              />
              <SaveButton
                onClick={handleTestEmail}
                disabled={!testEmailData.email}
              >
                Send Test
              </SaveButton>
            </div>
        </div>
      </EnhancedModal>

      {/* Delete Modal */}
      <EnhancedModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setTemplateToDelete(null);
        }}
        title="Delete Template"
        size="small"
        closeOnEscape={true}
        closeOnOutsideClick={true}
        showCloseButton={true}
      >
        <div>
            <p className="text-sm text-gray-500 mb-4">
              Are you sure you want to delete <strong>{templateToDelete?.name}</strong>? 
              This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setTemplateToDelete(null);
                }}
                className="px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteTemplate}
                className="bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors px-3 py-1.5 text-sm"
              >
                Delete
              </button>
            </div>
        </div>
      </EnhancedModal>
    </div>
  );
};

export default EmailTemplates;